import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
    IconCopy,
    IconFileTypePdf,
    IconRefresh,
    IconShare2,
} from '@tabler/icons-react';
import { notifications } from '@/lib/notifications';
import { useTranslation } from '../../../../../i18n/hooks/useTranslation';
import { toBnDigits } from '../../../../../utils/bnFormat';
import { recordToolUsage } from '../../../../../services/toolsService';
import ToolPageLayout from '../../components/common/ToolPageLayout';
import ToolPreviewPanel from '../../components/common/ToolPreviewPanel';
import ToolCard from '../../components/common/ToolCard';
import {
    copyTextToClipboard,
    deriveToolStatus,
    getToolStatusText,
} from '../../toolPageUtils';
import {
    BMI_CATEGORY_STYLES,
    calculateBmi,
    formatWeightDisplay,
    heightToImperial,
    imperialToHeightCm,
    validateBmiInputs,
    weightToKg,
} from './bmiCalculatorEngine';
import {
    buildBmiCalculatorShareUrl,
    parseBmiCalculatorShareParams,
} from './bmiCalculatorShare';
import { downloadBmiCalculatorPdf } from './generateBmiCalculatorExport';

const TOOL_PATH = '/tools/general/bmi-calculator';

const inputClass = 'w-full rounded-xl border border-[#ECE8E4] bg-white px-4 py-3 text-[15px] font-semibold text-[#3D3833] outline-none transition-colors focus:border-orange-400 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-100';
const inputErrorClass = 'border-red-500 dark:border-red-500';

const CATEGORY_SCALE = [
    { key: 'underweight', min: 0, max: 18.5 },
    { key: 'normal', min: 18.5, max: 25 },
    { key: 'overweight', min: 25, max: 30 },
    { key: 'obese', min: 30, max: 40 },
];

function bnToAr(str) {
    return String(str).replace(/[০-৯]/g, (ch) => String.fromCharCode(ch.charCodeAt(0) - 0x09e6 + 48));
}

function formatNumber(value, lang, decimals = 1) {
    const num = typeof value === 'number' ? value : Number(value);
    const str = Number.isInteger(num) && decimals === 0 ? String(num) : num.toFixed(decimals);
    if (lang !== 'bn') return str;
    return toBnDigits(str);
}

function formatWeight(valueKg, weightUnit, lang) {
    if (!Number.isFinite(valueKg)) return '';
    if (weightUnit === 'lb') {
        const lb = valueKg / 0.45359237;
        return `${formatNumber(lb, lang, 1)} ${lang === 'bn' ? 'পাউন্ড' : 'lb'}`;
    }
    return `${formatNumber(valueKg, lang, 1)} ${lang === 'bn' ? 'কেজি' : 'kg'}`;
}

function formatHeight(heightCm, heightUnit, lang) {
    if (!Number.isFinite(heightCm)) return '';
    if (heightUnit === 'ftin') {
        const { feet, inches } = heightToImperial(heightCm);
        return `${formatNumber(Number(feet), lang, 0)}′ ${formatNumber(Number(inches), lang, 0)}″`;
    }
    return `${formatNumber(heightCm, lang, 0)} ${lang === 'bn' ? 'সেমি' : 'cm'}`;
}

function LocalizedNumberInput({
    value,
    onChange,
    lang,
    allowDecimal = false,
    placeholder = '',
    error,
    id,
    suffix,
}) {
    const isBn = lang === 'bn';

    const fmt = (v) => {
        if (v === '' || v === null || v === undefined) return '';
        return isBn ? toBnDigits(String(v)) : String(v);
    };

    const [display, setDisplay] = useState(() => fmt(value));
    const prevValueRef = useRef(value);

    useEffect(() => {
        if (prevValueRef.current !== value) {
            prevValueRef.current = value;
            setDisplay(fmt(value));
        }
    }, [value]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        setDisplay((prev) => {
            const ar = bnToAr(prev);
            return isBn ? toBnDigits(ar) : ar;
        });
    }, [isBn]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleChange = (e) => {
        const raw = bnToAr(e.target.value);
        const cleaned = allowDecimal
            ? raw.replace(/[^\d.]/g, '').replace(/(\..*)\./g, '$1')
            : raw.replace(/\D/g, '');

        setDisplay(isBn ? toBnDigits(cleaned) : cleaned);

        if (cleaned === '' || cleaned === '.') {
            if (value !== '') onChange('');
        } else if (!cleaned.endsWith('.')) {
            const n = Number(cleaned);
            if (!Number.isNaN(n)) onChange(n);
        }
    };

    return (
        <div>
            <input
                id={id}
                type="text"
                inputMode={allowDecimal ? 'decimal' : 'numeric'}
                value={display}
                onChange={handleChange}
                placeholder={placeholder}
                className={`${inputClass} ${error ? inputErrorClass : ''}`}
            />
            {suffix ? (
                <p className="mt-1 text-[11px] text-[#6B635C] dark:text-zinc-500">{suffix}</p>
            ) : null}
        </div>
    );
}

function UnitToggle({ value, onChange, metricLabel, imperialLabel, metricId = 'metric', imperialId = 'imperial' }) {
    return (
        <div className="inline-flex gap-0.5 rounded-lg border border-[#ECE8E4] bg-[#F7F7F9] p-0.5 dark:border-zinc-700 dark:bg-zinc-800">
            {[
                { id: metricId, label: metricLabel },
                { id: imperialId, label: imperialLabel },
            ].map(({ id, label }) => {
                const active = value === id;
                return (
                    <button
                        key={id}
                        type="button"
                        onClick={() => onChange(id)}
                        className={`rounded-md px-3 py-1.5 text-[11px] font-semibold transition-colors ${
                            active
                                ? 'bg-orange-500 text-white'
                                : 'text-[#6B635C] hover:text-[#3D3833] dark:text-zinc-400 dark:hover:text-zinc-200'
                        }`}
                    >
                        {label}
                    </button>
                );
            })}
        </div>
    );
}

function BmiScaleBar({ bmi, category, t }) {
    const markerPercent = Math.min(100, Math.max(0, (bmi / 40) * 100));

    return (
        <div className="space-y-3">
            <div className="flex h-3 overflow-hidden rounded-full">
                {CATEGORY_SCALE.map((seg) => {
                    const width = ((seg.max - seg.min) / 40) * 100;
                    const style = BMI_CATEGORY_STYLES[seg.key];
                    return (
                        <div
                            key={seg.key}
                            className={`${style.bar} h-full`}
                            style={{ width: `${width}%` }}
                            title={t(`bmiCalculator.categories.${seg.key}.label`)}
                        />
                    );
                })}
            </div>
            <div className="relative h-4">
                <div
                    className="absolute top-0 h-4 w-4 -translate-x-1/2 rounded-full border-2 border-white bg-orange-600 shadow-md dark:border-zinc-900"
                    style={{ left: `${markerPercent}%` }}
                />
            </div>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                {CATEGORY_SCALE.map((seg) => {
                    const style = BMI_CATEGORY_STYLES[seg.key];
                    const active = category === seg.key;
                    return (
                        <div
                            key={seg.key}
                            className={`rounded-lg border px-2 py-1.5 text-center text-[10px] font-semibold ${
                                active
                                    ? `${style.bg} ${style.border} ${style.text}`
                                    : 'border-[#ECE8E4] text-[#9A9AA3] dark:border-zinc-700 dark:text-zinc-500'
                            }`}
                        >
                            {t(`bmiCalculator.categories.${seg.key}.label`)}
                            <span className="mt-0.5 block font-normal opacity-80">
                                {t(`bmiCalculator.categories.${seg.key}.range`)}
                            </span>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

export default function BmiCalculatorGeneral() {
    const { t, currentLanguage } = useTranslation();
    const lang = currentLanguage === 'bn' ? 'bn' : 'en';
    const location = useLocation();
    const usageRecordedRef = useRef(false);
    const exportRef = useRef(null);
    const [exporting, setExporting] = useState(false);

    const [weightUnit, setWeightUnit] = useState('kg');
    const [heightUnit, setHeightUnit] = useState('cm');
    const [weight, setWeight] = useState('');
    const [heightCm, setHeightCm] = useState('');
    const [heightFt, setHeightFt] = useState('');
    const [heightIn, setHeightIn] = useState('');

    useEffect(() => {
        const search = location.search;
        if (!search) return;
        const parsed = parseBmiCalculatorShareParams(search);
        if (parsed.weightUnit) setWeightUnit(parsed.weightUnit);
        if (parsed.heightUnit) setHeightUnit(parsed.heightUnit);
        if (parsed.weightKg) {
            setWeight(
                parsed.weightUnit === 'lb'
                    ? Number(formatWeightDisplay(parsed.weightKg, 'imperial'))
                    : parsed.weightKg,
            );
        }
        if (parsed.heightCm) {
            if (parsed.heightUnit === 'ftin') {
                const imp = heightToImperial(parsed.heightCm);
                setHeightFt(Number(imp.feet) || '');
                setHeightIn(Number(imp.inches) || '');
                setHeightCm('');
            } else {
                setHeightCm(parsed.heightCm);
                setHeightFt('');
                setHeightIn('');
            }
        }
    }, [location.search]); // eslint-disable-line react-hooks/exhaustive-deps

    const resolvedHeightCm = useMemo(() => {
        if (heightUnit === 'cm') {
            return heightCm === '' ? NaN : Number(heightCm);
        }
        if (heightFt === '' && heightIn === '') return NaN;
        return imperialToHeightCm(Number(heightFt) || 0, Number(heightIn) || 0);
    }, [heightUnit, heightCm, heightFt, heightIn]);

    const weightKg = useMemo(() => {
        if (weight === '') return NaN;
        return weightToKg(Number(weight), weightUnit === 'lb' ? 'imperial' : 'metric');
    }, [weight, weightUnit]);

    const isFormEmpty = weight === '' && (heightUnit === 'cm' ? heightCm === '' : heightFt === '' && heightIn === '');

    const validation = useMemo(
        () => validateBmiInputs(weightKg, resolvedHeightCm),
        [weightKg, resolvedHeightCm],
    );

    const result = useMemo(() => {
        if (!validation.valid) return null;
        return calculateBmi(weightKg, resolvedHeightCm);
    }, [validation, weightKg, resolvedHeightCm]);

    const inputError = useMemo(() => {
        if (validation.valid || isFormEmpty) return null;
        return t(`bmiCalculator.errors.${validation.error}`);
    }, [validation, isFormEmpty, t]);

    const recordUsageOnce = () => {
        if (usageRecordedRef.current) return;
        usageRecordedRef.current = true;
        recordToolUsage('general/bmi-calculator', { category: 'general' });
    };

    useEffect(() => {
        if (result?.ok) recordUsageOnce();
    }, [result?.ok]);

    const handleWeightUnitChange = (nextUnit) => {
        if (nextUnit === weightUnit) return;
        if (Number.isFinite(weightKg)) {
            setWeight(
                nextUnit === 'lb'
                    ? Number(formatWeightDisplay(weightKg, 'imperial'))
                    : Math.round(weightKg * 10) / 10,
            );
        }
        setWeightUnit(nextUnit);
    };

    const handleHeightUnitChange = (nextUnit) => {
        if (nextUnit === heightUnit) return;
        if (nextUnit === 'ftin' && Number.isFinite(resolvedHeightCm)) {
            const imp = heightToImperial(resolvedHeightCm);
            setHeightFt(imp.feet === '' ? '' : Number(imp.feet));
            setHeightIn(imp.inches === '' ? '' : Number(imp.inches));
            setHeightCm('');
        } else if (nextUnit === 'cm' && Number.isFinite(resolvedHeightCm)) {
            setHeightCm(Math.round(resolvedHeightCm * 10) / 10);
            setHeightFt('');
            setHeightIn('');
        }
        setHeightUnit(nextUnit);
    };

    const handleReset = () => {
        setWeight('');
        setHeightCm('');
        setHeightFt('');
        setHeightIn('');
        setWeightUnit('kg');
        setHeightUnit('cm');
        usageRecordedRef.current = false;
        window.history.replaceState(null, '', TOOL_PATH);
    };

    const resultsText = useMemo(() => {
        if (!result?.ok) return '';
        const cat = t(`bmiCalculator.categories.${result.category}.label`);
        return [
            `${t('bmiCalculator.yourBmi')}: ${formatNumber(result.bmi, lang, 1)}`,
            `${t('bmiCalculator.category')}: ${cat}`,
            `${t('bmiCalculator.weight')}: ${formatWeight(result.weightKg, weightUnit, lang)}`,
            `${t('bmiCalculator.height')}: ${formatHeight(result.heightCm, heightUnit, lang)}`,
            `${t('bmiCalculator.healthyRange')}: ${formatWeight(result.healthyMinKg, weightUnit, lang)} – ${formatWeight(result.healthyMaxKg, weightUnit, lang)}`,
        ].join('\n');
    }, [result, lang, weightUnit, heightUnit, t]);

    const handleCopy = () => {
        if (!resultsText) return;
        if (copyTextToClipboard(resultsText)) {
            notifications.show({ title: t('common.success'), message: t('bmiCalculator.copied'), color: 'green' });
            return;
        }
        void navigator.clipboard?.writeText(resultsText)
            .then(() => notifications.show({ title: t('common.success'), message: t('bmiCalculator.copied'), color: 'green' }))
            .catch(() => notifications.show({ title: t('common.error'), message: t('bmiCalculator.copyFailed'), color: 'red' }));
    };

    const handleShare = async () => {
        if (!validation.valid) {
            notifications.show({ title: t('common.error'), message: t('bmiCalculator.shareRequiresInput'), color: 'yellow' });
            return;
        }
        const url = buildBmiCalculatorShareUrl(window.location.origin, TOOL_PATH, {
            weightUnit,
            heightUnit,
            weightKg,
            heightCm: resolvedHeightCm,
        });
        const copied = copyTextToClipboard(url);
        if (typeof navigator !== 'undefined' && navigator.share) {
            try {
                await navigator.share({ title: t('bmiCalculator.shareTitle'), text: t('bmiCalculator.shareText'), url });
                notifications.show({ title: t('common.success'), message: t('bmiCalculator.shared'), color: 'green' });
                return;
            } catch (err) {
                if (err?.name === 'AbortError') return;
            }
        }
        if (copied) {
            notifications.show({ title: t('common.success'), message: t('bmiCalculator.linkCopied'), color: 'green' });
        } else {
            notifications.show({ title: t('common.error'), message: t('bmiCalculator.shareFailed'), color: 'red' });
        }
    };

    const status = deriveToolStatus({
        hasInput: !isFormEmpty,
        canRun: validation.valid,
        hasResult: Boolean(result?.ok),
    });

    const statusText = getToolStatusText(status, t, {
        ready: t('bmiCalculator.statusReady'),
        success: t('bmiCalculator.statusSuccess'),
    });

    const handleDownloadPdf = async () => {
        if (!exportRef.current) return;
        setExporting(true);
        try {
            await downloadBmiCalculatorPdf(exportRef.current, `bmi-calculator-${Date.now()}.pdf`);
            notifications.show({ title: t('common.success'), message: t('bmiCalculator.pdfDownloaded'), color: 'green' });
        } catch {
            notifications.show({ title: t('common.error'), message: t('bmiCalculator.exportFailed'), color: 'red' });
        } finally {
            setExporting(false);
        }
    };

    const howToSteps = useMemo(
        () => [1, 2, 3, 4].map((i) => t(`bmiCalculator.howTo.step${i}`)),
        [t],
    );

    const categoryStyle = result?.ok ? BMI_CATEGORY_STYLES[result.category] : null;

    return (
        <ToolPageLayout
            slug="general/bmi-calculator"
            subtitleKey="bmiCalculator.subtitle"
            status={status}
            statusText={statusText}
            controlsTitle={t('bmiCalculator.settingsTitle')}
            howToSteps={howToSteps}
            onReset={!isFormEmpty ? handleReset : undefined}
            resetLabel={t('bmiCalculator.reset')}
            controls={(
                <div className="space-y-4">
                    <div>
                        <div className="mb-1.5 flex items-center justify-between gap-3">
                            <label htmlFor="bmi-weight" className="text-[12px] font-semibold text-[#3D3833] dark:text-zinc-300">
                                {t('bmiCalculator.weight')}
                                <span className="text-red-500"> *</span>
                            </label>
                            <UnitToggle
                                value={weightUnit}
                                onChange={handleWeightUnitChange}
                                metricLabel={t('bmiCalculator.kilograms')}
                                imperialLabel={t('bmiCalculator.pounds')}
                                metricId="kg"
                                imperialId="lb"
                            />
                        </div>
                        <LocalizedNumberInput
                            id="bmi-weight"
                            value={weight}
                            onChange={setWeight}
                            lang={lang}
                            allowDecimal
                            placeholder={weightUnit === 'kg' ? t('bmiCalculator.weightPlaceholderMetric') : t('bmiCalculator.weightPlaceholderImperial')}
                            error={inputError && validation.error === 'invalidWeight'}
                            suffix={weightUnit === 'kg' ? t('bmiCalculator.weightHintMetric') : t('bmiCalculator.weightHintImperial')}
                        />
                    </div>

                    <div>
                        <div className="mb-1.5 flex items-center justify-between gap-3">
                            <label className="text-[12px] font-semibold text-[#3D3833] dark:text-zinc-300">
                                {t('bmiCalculator.height')}
                                <span className="text-red-500"> *</span>
                            </label>
                            <UnitToggle
                                value={heightUnit}
                                onChange={handleHeightUnitChange}
                                metricLabel={t('bmiCalculator.centimeters')}
                                imperialLabel={t('bmiCalculator.feetInches')}
                                metricId="cm"
                                imperialId="ftin"
                            />
                        </div>
                        {heightUnit === 'cm' ? (
                            <LocalizedNumberInput
                                id="bmi-height-cm"
                                value={heightCm}
                                onChange={setHeightCm}
                                lang={lang}
                                placeholder={t('bmiCalculator.heightPlaceholderMetric')}
                                error={inputError && validation.error === 'invalidHeight'}
                                suffix={t('bmiCalculator.heightHintMetric')}
                            />
                        ) : (
                            <div className="grid grid-cols-2 gap-2">
                                <LocalizedNumberInput
                                    id="bmi-height-ft"
                                    value={heightFt}
                                    onChange={setHeightFt}
                                    lang={lang}
                                    placeholder={t('bmiCalculator.heightPlaceholderFeet')}
                                    error={inputError && validation.error === 'invalidHeight'}
                                    suffix={t('bmiCalculator.feet')}
                                />
                                <LocalizedNumberInput
                                    id="bmi-height-in"
                                    value={heightIn}
                                    onChange={setHeightIn}
                                    lang={lang}
                                    placeholder={t('bmiCalculator.heightPlaceholderInches')}
                                    error={inputError && validation.error === 'invalidHeight'}
                                    suffix={t('bmiCalculator.inches')}
                                />
                            </div>
                        )}
                    </div>

                    {inputError ? (
                        <p className="text-[12px] font-medium text-red-600 dark:text-red-400" role="alert">
                            {inputError}
                        </p>
                    ) : null}
                </div>
            )}
            settings={result?.ok ? (
                <div className="space-y-3">
                    <button
                        type="button"
                        onClick={handleReset}
                        className="flex w-full items-center justify-center gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-[12px] font-semibold text-red-600 transition-colors hover:bg-red-100 dark:border-red-900/40 dark:bg-red-950/20 dark:text-red-400"
                    >
                        <IconRefresh size={14} />
                        {t('bmiCalculator.reset')}
                    </button>
                    <button
                        type="button"
                        onClick={handleCopy}
                        className="flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-b from-orange-500 to-orange-600 px-4 py-3 text-[14px] font-semibold text-white shadow-md transition-opacity hover:opacity-95"
                    >
                        <IconCopy size={16} />
                        {t('bmiCalculator.copyResults')}
                    </button>
                    <ToolCard title={t('bmiCalculator.exportShareTitle')}>
                        <div className="grid grid-cols-2 gap-1.5">
                            <button
                                type="button"
                                onClick={handleDownloadPdf}
                                disabled={exporting}
                                className="flex min-h-[58px] flex-col items-center justify-center gap-1 rounded-[10px] border border-[#E8E8EE] bg-[#F7F7F9] px-1.5 py-2.5 text-[10px] font-semibold leading-tight text-[#3A3A42] transition-colors hover:border-orange-300 disabled:cursor-not-allowed disabled:opacity-60 dark:border-zinc-700 dark:bg-zinc-800 dark:text-zinc-300"
                            >
                                <IconFileTypePdf size={15} className="text-primary" stroke={1.8} />
                                {exporting ? t('bmiCalculator.downloading') : t('bmiCalculator.downloadPdf')}
                            </button>
                            <button
                                type="button"
                                onClick={handleShare}
                                className="flex min-h-[58px] flex-col items-center justify-center gap-1 rounded-[10px] border border-[#E8E8EE] bg-[#F7F7F9] px-1.5 py-2.5 text-[10px] font-semibold leading-tight text-[#3A3A42] transition-colors hover:border-orange-300 dark:border-zinc-700 dark:bg-zinc-800 dark:text-zinc-300"
                            >
                                <IconShare2 size={15} className="text-primary" stroke={1.8} />
                                {t('bmiCalculator.share')}
                            </button>
                        </div>
                    </ToolCard>
                </div>
            ) : null}
            preview={(
                <ToolPreviewPanel
                    title={t('bmiCalculator.previewTitle')}
                    status={status}
                    statusLabel={t(`toolPage.status.${status}`)}
                    idleTitle={t('bmiCalculator.enterDetails')}
                    idleHint={t('bmiCalculator.previewEmptyHint')}
                    showIdle={!result?.ok}
                    tall
                    fill
                    flushContent
                >
                    {result?.ok && categoryStyle ? (
                        <div
                            ref={exportRef}
                            data-bmi-calc-export
                            className="min-h-0 flex-1 space-y-4 overflow-y-auto rounded-lg bg-[#FBFAF9] p-3 dark:bg-zinc-950 sm:p-4"
                        >
                            <div className="rounded-2xl border border-orange-200 bg-orange-50 px-4 py-6 text-center dark:border-orange-900/40 dark:bg-orange-950/20">
                                <p className="mb-4 text-[11px] font-bold uppercase tracking-[0.12em] text-[#6B635C] dark:text-zinc-400">
                                    {t('bmiCalculator.yourBmi')}
                                </p>
                                <p className={`text-[40px] font-black leading-none sm:text-[48px] ${categoryStyle.text}`}>
                                    {formatNumber(result.bmi, lang, 1)}
                                </p>
                                <span className={`mt-4 inline-flex rounded-full border px-4 py-1.5 text-[13px] font-bold ${categoryStyle.bg} ${categoryStyle.border} ${categoryStyle.text}`}>
                                    {t(`bmiCalculator.categories.${result.category}.label`)}
                                </span>
                                <p className="mx-auto mt-4 max-w-xs text-[12.5px] leading-relaxed text-[#6B635C] dark:text-zinc-400">
                                    {t(`bmiCalculator.categories.${result.category}.description`)}
                                </p>
                            </div>

                            <div className="rounded-2xl border border-[#ECE8E4] bg-white p-4 dark:border-zinc-700 dark:bg-zinc-900">
                                <p className="mb-3 text-[13px] font-semibold text-[#3D3833] dark:text-zinc-200">
                                    {t('bmiCalculator.scaleTitle')}
                                </p>
                                <BmiScaleBar bmi={result.bmi} category={result.category} t={t} />
                            </div>

                            <div className="grid grid-cols-1 gap-3">
                                <div className="rounded-2xl border border-orange-200 bg-orange-50 px-4 py-4 dark:border-orange-900/40 dark:bg-orange-950/20">
                                    <p className="text-[11px] font-semibold uppercase tracking-wide text-orange-700/80 dark:text-orange-300/80">
                                        {t('bmiCalculator.healthyRange')}
                                    </p>
                                    <p className="mt-2 text-[18px] font-bold text-orange-800 dark:text-orange-200">
                                        {formatWeight(result.healthyMinKg, weightUnit, lang)}
                                        {' – '}
                                        {formatWeight(result.healthyMaxKg, weightUnit, lang)}
                                    </p>
                                    <p className="mt-1 text-[12px] text-[#6B635C] dark:text-zinc-400">
                                        {t('bmiCalculator.healthyRangeHint')}
                                    </p>
                                </div>
                                <div className="rounded-2xl border border-[#ECE8E4] bg-white px-4 py-4 dark:border-zinc-700 dark:bg-zinc-900">
                                    <p className="text-[11px] font-semibold uppercase tracking-wide text-[#6B635C] dark:text-zinc-400">
                                        {t('bmiCalculator.yourDetails')}
                                    </p>
                                    <div className="mt-3 space-y-2 text-[13px]">
                                        <div className="flex justify-between gap-3">
                                            <span className="text-[#6B635C] dark:text-zinc-400">{t('bmiCalculator.weight')}</span>
                                            <span className="font-semibold text-[#1C1815] dark:text-zinc-100">{formatWeight(result.weightKg, weightUnit, lang)}</span>
                                        </div>
                                        <div className="flex justify-between gap-3">
                                            <span className="text-[#6B635C] dark:text-zinc-400">{t('bmiCalculator.height')}</span>
                                            <span className="font-semibold text-[#1C1815] dark:text-zinc-100">{formatHeight(result.heightCm, heightUnit, lang)}</span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="rounded-2xl border border-[#ECE8E4] bg-white p-4 dark:border-zinc-700 dark:bg-zinc-900">
                                <p className="mb-3 text-[13px] font-semibold text-[#3D3833] dark:text-zinc-200">
                                    {t('bmiCalculator.insightsTitle')}
                                </p>
                                <div className="grid grid-cols-1 gap-3">
                                    <div className="rounded-xl border border-[#ECE8E4] bg-white px-4 py-3 dark:border-zinc-700 dark:bg-zinc-900">
                                        <p className="text-[13px] font-bold text-orange-800 dark:text-orange-300">{t('bmiCalculator.insightFormulaTitle')}</p>
                                        <p className="mt-1.5 text-[12px] leading-relaxed text-[#6B635C] dark:text-zinc-400">{t('bmiCalculator.insightFormulaBody')}</p>
                                    </div>
                                    <div className="rounded-xl border border-[#ECE8E4] bg-white px-4 py-3 dark:border-zinc-700 dark:bg-zinc-900">
                                        <p className="text-[13px] font-bold text-orange-800 dark:text-orange-300">{t('bmiCalculator.insightLimitTitle')}</p>
                                        <p className="mt-1.5 text-[12px] leading-relaxed text-[#6B635C] dark:text-zinc-400">{t('bmiCalculator.insightLimitBody')}</p>
                                    </div>
                                    <div className="rounded-xl border border-[#ECE8E4] bg-white px-4 py-3 dark:border-zinc-700 dark:bg-zinc-900">
                                        <p className="text-[13px] font-bold text-orange-800 dark:text-orange-300">{t('bmiCalculator.insightTipsTitle')}</p>
                                        <p className="mt-1.5 text-[12px] leading-relaxed text-[#6B635C] dark:text-zinc-400">{t('bmiCalculator.insightTipsBody')}</p>
                                    </div>
                                    <div className="rounded-xl border border-[#ECE8E4] bg-white px-4 py-3 dark:border-zinc-700 dark:bg-zinc-900">
                                        <p className="text-[13px] font-bold text-orange-800 dark:text-orange-300">{t('bmiCalculator.insightHealthTitle')}</p>
                                        <p className="mt-1.5 text-[12px] leading-relaxed text-[#6B635C] dark:text-zinc-400">{t('bmiCalculator.insightHealthBody')}</p>
                                    </div>
                                </div>
                            </div>

                            <p className="text-center text-[11px] text-[#9ca3af] dark:text-zinc-600">sohozkaj.com</p>
                        </div>
                    ) : null}
                </ToolPreviewPanel>
            )}
        />
    );
}
